import { Box, useTheme,IconButton, ButtonBase } from "@mui/material"
import { useState, useEffect } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CheckIcon from '@mui/icons-material/Check';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import NumberInput from "../../theme/inputs/NumberInput";
import { useTranslation } from "react-i18next";
import { useAddChildProductsMutation, useGetProductsSelectQuery } from "../../../newapi/warehouse/productSlice";
import { showToastMessagePostRequest, showToastMessage, showToastMessageRequestError } from "../../../utils/toasts";
import DeleteIcon from '@mui/icons-material/Delete';
import { DropdownBase } from "../../theme/dropdowns/Dropdown";
import { useSelector } from 'react-redux';
import { ENV_TYPES } from "../Sidebar";
import { DropdownBaseV2 } from "../../theme/dropdowns/Dropdown";
import { countriesFromLocale, quickSelect } from "../../../utils/countries";
import cx from 'classnames';

const MCountrySelector = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);

    const language = useSelector((state) => state.settings.language);
    const countries = countriesFromLocale(language);

    const [selectedCountries,setSelectedCountries] = useState(() => {
        // Initialize selected countries from props on mount
        if (!props.countries || !countries) return [];

        let countryCodes = [];

        if (Array.isArray(props.countries)) {
            // Check if it's an array of objects or an array of strict country codes
            if (props.countries.length > 0) {
                if (typeof props.countries[0] === 'string') {
                    // Array of strict country codes
                    countryCodes = props.countries;
                } else if (typeof props.countries[0] === 'object') {
                    // Array of objects - extract alpha2 codes
                    countryCodes = props.countries
                        .map(item => item.alpha2 || item.id || item.code || item.countryCode)
                        .filter(Boolean);
                }
            }
        }

        // Validate that the codes exist in our countries list
        const validCodes = countryCodes.filter(code =>
            countries.some(c => c.alpha2 === code)
        );

        return validCodes;
    });

    const handleClose = () => {
        props.handleClose();
    }

    const handleSelect = () => {
        if (props.onSelect) {
            // Return array of country objects with alpha2 and name
            const selectedCountryObjects = selectedCountries.map(countryCode => {
                const country = countries.find(c => c.alpha2 === countryCode);
                return {
                    id: countryCode,
                    name: country?.name || countryCode,
                };
            });
            props.onSelect(selectedCountryObjects);
        }
        handleClose();
    }
      
    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }
    
    const handleCountryChange = (selectedIndices) => {
        if (!countries) return;

        const selectedCountryCodes = selectedIndices.map(index => countries[index].alpha2);
        setSelectedCountries(selectedCountryCodes);
    };

    const getSelectedCountryIndices = () => {
        if (!countries || !selectedCountries.length) return [];

        return selectedCountries
            .map(countryCode => countries.findIndex(c => c.alpha2 === countryCode))
            .filter(index => index !== -1);
    };
        
    const getSelectedCountryNames = () => {
        if (!countries || !selectedCountries.length) return [];

        return selectedCountries
            .map(countryCode => {
                const country = countries.find(c => c.alpha2 === countryCode);
                return country ? country.name : null;
            })
            .filter(name => name !== null);
    };

    const selectQuickRegion = (regionIndex) => {
        const countryCodes = quickSelect[regionIndex]?.countries || [];

        // map codes to indices (alpha2 codes are already lowercase in both arrays)
        const selectedIndices = countryCodes
            .map(code => countries.findIndex(c => c.alpha2 === code))
            .filter(index => index !== -1);

        // check if currently selected countries match this region
        const currentlySelectedIndices = getSelectedCountryIndices();
        const isSameSelection =
            selectedIndices.length === currentlySelectedIndices.length &&
            selectedIndices.every(index => currentlySelectedIndices.includes(index));

        if (isSameSelection) {
            // same region clicked → deselect all
            handleCountryChange([]);
        } else {
            // different region → replace selection
            handleCountryChange(selectedIndices);
        }
    };

    const selectAllCountries = () => {
        if (!countries) return;

        // Same toggle as the regions: clicking it while everything is picked clears the selection.
        if (selectedCountries.length === countries.length) {
            handleCountryChange([]);
            return;
        }

        handleCountryChange(countries.map((country, index) => index));
    };

    const isAllCountriesActive = () => {
        return countries?.length > 0 && selectedCountries.length === countries.length;
    };

    const isQuickRegionActive = (regionIndex) => {
        const countryCodes = quickSelect[regionIndex]?.countries || [];

        const regionIndices = countryCodes
            .map(code => countries.findIndex(c => c.alpha2 === code))
            .filter(index => index !== -1);

        const currentlySelectedIndices = getSelectedCountryIndices();

        return (
            currentlySelectedIndices.length > 0 &&
            regionIndices.length === currentlySelectedIndices.length &&
            regionIndices.every(index => currentlySelectedIndices.includes(index))
        );
    };
            

    return(
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign:"center",
                    paddingY:4,
                    paddingX:3,
                    display:"flex",
                    flexDirection:"column",
                    gap:2,
                }}>
                <Subheading>{t("select_one_or_multiple_countries")}</Subheading>

                {/* Country Dropdown */}
                <DropdownBaseV2
                    multiple={true}
                    options={countries ?? []}
                    selected={getSelectedCountryIndices()}
                    displayName="name"
                    onChange={(selectedIndices) => {
                        handleCountryChange(selectedIndices);
                    }}
                    searchOptions={{
                        enabled: true,
                        placeholder: t("search")
                    }}
                    closeOnSelection={false}
                >
                    {({anchorEl,onClick}) => (
                        <ButtonBase
                            onClick={onClick}
                            sx={{
                                width:1,
                                background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                                color:theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"],
                                fontSize:14,
                                fontWeight:500,
                                fontFamily:"Roboto, sans-serif",
                                height:40,
                                borderRadius:5,
                            }}
                        >
                            {t("insert")}
                        </ButtonBase>
                    )}
                </DropdownBaseV2>

                {/* Quick Select Buttons */}
                <Box
                    sx={{
                        display:"flex",
                        gap:1.5,
                        flexDirection:'column',
                        alignItems:"flex-start",
                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200],
                        p:1.5,
                        borderRadius:4,
                    }}
                >
                    {t("quick_select")}
                    <Box
                        sx={{
                            width:1,
                            display:"grid",
                            gridTemplateColumns:"1fr 1fr 1fr",
                            gap:"8px 12px",
                        }}
                    >
                        {quickSelect.slice(0, 3).map((region, idx) => (
                            <ButtonBase
                                key={idx}
                                className={cx(undefined, {
                                    'is-active': isQuickRegionActive(idx),
                                })}
                                onClick={() => selectQuickRegion(idx)}
                                sx={{
                                    px:1,
                                    height:24,
                                    borderRadius:4,
                                    justifyContent:'center',
                                    alignItems:'center',
                                    background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                    color:colors.txt['primary'],
                                    fontWeight:500,
                                    fontSize:14,
                                    fontFamily:"Roboto,sans-serif",

                                    "& .region-icon" : {
                                        display:"none",
                                    },

                                    "&:hover" : {
                                        background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[50],
                                    },
                                    "&.is-active" : {
                                        background:theme.palette.mode=== "dark" ? colors.blue[100] : colors.primary[100],
                                        borderColor:theme.palette.mode=== "dark" ? colors.blue[400] : colors.primary[400],
                                        color:theme.palette.mode=== "dark" ? colors.blue[400] : colors.primary[400],
                                        justifyContent:'space-between',
                                        "& .region-icon" : {
                                            display:"block",
                                        },
                                    },
                                }}
                            >
                                {region.name}
                                <CheckIcon fontSize='small' className="region-icon"/>
                            </ButtonBase>
                        ))}
                    </Box>
                    <Box
                        sx={{
                            width:1,
                            display:"grid",
                            gridTemplateColumns:"1fr 1fr",
                            gap:"8px 12px",
                        }}
                    >
                        {quickSelect.slice(3, 5).map((region, idx) => (
                            <ButtonBase
                                key={idx + 3}
                                className={cx(undefined, {
                                    'is-active': isQuickRegionActive(idx + 3),
                                })}
                                onClick={() => selectQuickRegion(idx + 3)}
                                sx={{
                                    px:1,
                                    height:24,
                                    borderRadius:4,
                                    justifyContent:'center',
                                    alignItems:'center',
                                    background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                    color:colors.txt['primary'],
                                    fontWeight:500,
                                    fontSize:14,
                                    fontFamily:"Roboto,sans-serif",

                                    "& .region-icon" : {
                                        display:"none",
                                    },

                                    "&:hover" : {
                                        background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[50],
                                    },
                                    "&.is-active" : {
                                        background:theme.palette.mode=== "dark" ? colors.blue[100] : colors.primary[100],
                                        borderColor:theme.palette.mode=== "dark" ? colors.blue[400] : colors.primary[400],
                                        color:theme.palette.mode=== "dark" ? colors.blue[400] : colors.primary[400],
                                        justifyContent:'space-between',
                                        "& .region-icon" : {
                                            display:"block",
                                        },
                                    },
                                }}
                            >
                                {region.name}
                                <CheckIcon fontSize='small' className="region-icon"/>
                            </ButtonBase>
                        ))}
                    </Box>
                    <ButtonBase
                        className={cx(undefined, {
                            'is-active': isAllCountriesActive(),
                        })}
                        onClick={selectAllCountries}
                        sx={{
                            width:1,
                            px:1,
                            height:24,
                            borderRadius:4,
                            justifyContent:'center',
                            alignItems:'center',
                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                            color:colors.txt['primary'],
                            fontWeight:500,
                            fontSize:14,
                            fontFamily:"Roboto,sans-serif",

                            "& .region-icon" : {
                                display:"none",
                            },

                            "&:hover" : {
                                background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[50],
                            },
                            "&.is-active" : {
                                background:theme.palette.mode=== "dark" ? colors.blue[100] : colors.primary[100],
                                borderColor:theme.palette.mode=== "dark" ? colors.blue[400] : colors.primary[400],
                                color:theme.palette.mode=== "dark" ? colors.blue[400] : colors.primary[400],
                                justifyContent:'space-between',
                                "& .region-icon" : {
                                    display:"block",
                                },
                            },
                        }}
                    >
                        {t("all")}
                        <CheckIcon fontSize='small' className="region-icon"/>
                    </ButtonBase>
                </Box>
            </Box>

            {/* BODY */}
            <Box
                sx={{
                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding:3,
                    borderTopLeftRadius:16,
                    borderTopRightRadius:16,
                    display:"flex",
                    flexDirection:"column",
                    gap:2,
                    pb:0,
                }}
            >
                {selectedCountries.length > 0 ? (
                    <Box
                        sx={{
                            display:"flex",
                            flexDirection:"column",
                            gap:1,
                        }}
                    >
                        <Text bold>
                            {selectedCountries.length} {t("selected")}
                        </Text>
                        <Box
                            sx={{
                                display:"grid",
                                gridTemplateColumns:"1fr 1fr 1fr",
                                gap:1,
                                maxHeight:300,
                                overflow:"auto",
                            }}
                        >
                            {selectedCountries.map((countryCode, idx) => {
                                const country = countries.find(c => c.alpha2 === countryCode);
                                return (
                                    <ButtonBase
                                        key={idx}
                                        onClick={() => {
                                            // Remove this country from selection
                                            const newSelection = selectedCountries.filter(code => code !== countryCode);
                                            setSelectedCountries(newSelection);
                                        }}
                                        sx={{
                                            px:1.5,
                                            py:0.5,
                                            borderRadius:3,
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            fontSize:14,
                                            display:"flex",
                                            justifyContent:"space-between",
                                            alignItems:"center",
                                            gap:1,
                                            "&:hover": {
                                                background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[200],
                                            }
                                        }}
                                    >
                                        <Text>{country?.name}</Text>
                                        <DeleteIcon
                                            sx={{
                                                fontSize:16,
                                                color:colors.txt["secondary"],
                                            }}
                                        />
                                    </ButtonBase>
                                );
                            })}
                        </Box>
                    </Box>
                ): (
                    <Box
                        sx={{
                            display:"flex",
                            flexDirection:"column",
                            gap:1,
                        }}
                    >
                        <Text bold>
                            {t("no_selection")}
                        </Text>
                    </Box>
                )}
                
            </Box>
            <Box
                component="footer"
                sx={{
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"space-between",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    paddingTop:2,
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                    className={"group-btn"}
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    variant="contained"
                    onClick={handleSelect}
                    >{t("select")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MCountrySelector;
